import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authApi } from "../../config/api";
import { toast } from "react-toastify";

// Async thunk for signup
export const signup = createAsyncThunk(
  "auth/signup",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/auth/signup", userData);
      // toast.success(response.data?.message);
      return response.data;
    } catch (error) {
      // toast.error(error?.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/auth/signin", userData);
      // localStorage.getItem("accessToken", response.data.access_token);
      // document.cookie = `refresh_token=${response.data.refresh_token}; access_token=${response.data.access_token};`;
      document.cookie = `refresh_token=${response.data.refresh_token}; path=/;`;
      document.cookie = `access_token=${response.data.access_token}; path=/;`;
      const accessToken = response.data.access_token;
      const refreshToken = response.data.refresh_token;
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      // Save the access token
      authApi.defaults.headers.common["Authorization"] =
        "Bearer " + accessToken;
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/otpverify",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/auth/verify-otp/", userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendotp",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post("/auth/resend_otp", userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetpassword",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post(
        "/auth/password_reset_request",
        userData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const refreshToken = async (body) => {
  try {
    const response = await authApi.post("/auth/token/refreshtoken", body);

    localStorage.setItem("accessToken", response.data.access_token);
    return response.data.accessToken;
  } catch (error) {
    console.error("Failed to refresh token", error);
    throw error;
  }
};

export const signOut = createAsyncThunk(
  "auth/signout",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post(
        "/auth/token/refreshtoken/signout",
        userData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changepassword",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.post(
        "/auth/change-password-by-current-password",
        userData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const logoutAsync = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("Refresh token is missing");

      const response = await authApi.post("/auth/token/refreshtoken/signout", {
        refresh_token: refreshToken,
      });

      // Clear storage and cookies
      document.cookie =
        "refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
      document.cookie =
        "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    status: "idle",
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      localStorage.removeItem("accessToken");
    },
    resetStatus: (state) => {
      state.status = "idle"; // Reset the status back to "idle"
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.status = "loading";
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = "signup_succeeded";
        state.user = action.payload;
        state.error = null;
        toast.success(action.payload?.message);
        toast.info("otp has been sent to your register email");
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "signin_succeeded";
        state.user = action.payload;
        toast.success(action.payload?.message);
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = "verify_otp_succeeded";
        toast.success(action.payload?.message);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(resendOtp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.status = "succeeded";
        toast.success(action.payload?.message);
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = "reset_password_succeeded";
        toast.success(action.payload?.message);
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(signOut.pending, (state) => {
        state.status = "loading";
      })
      .addCase(signOut.fulfilled, (state, action) => {
        state.status = "signout_succesful";
        toast.success(action.payload?.message);
      })
      .addCase(signOut.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload?.message);
      })
      .addCase(changePassword.pending, (state) => {
        state.status = "loading";
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.status = "change_password_succeeded";
        toast.success(action.payload?.message);
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.status = "failed";
        toast.error(action.payload.message);
      });
  },
});

export const { logout, resetStatus } = authSlice.actions;

export default authSlice.reducer;
